import { useMemo, useState } from "react";
import { ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useApiSWR } from "@/hooks/useApiSWR";
import { formatTime } from "../types";
import { reportError } from "./utils";
import { TimeLogForm, type TimeLogValues } from "./TimeLogForm";
import { TimeLogRow } from "./TimeLogRow";
import {
	type TimeLog,
	listTimeLogs,
	createTimeLog,
	updateTimeLog,
	deleteTimeLog,
} from "@/services/time-log.service";

interface Props {
	projectId: string;
	taskId: string;
	assigneeIds: string[];
	onChange?: () => void;
}

function toPayload(v: TimeLogValues) {
	return {
		duration_minutes: v.minutes,
		description: v.note || null,
		logged_date: v.date,
	};
}

export function InlineLogTime({
	projectId,
	taskId,
	assigneeIds,
	onChange,
}: Props) {
	const { user } = useAuth();
	const roleKey = user?.global_role?.key ?? user?.role ?? null;
	const isAdminOrManager = roleKey === "admin" || roleKey === "manager";
	const canLog =
		!!user &&
		assigneeIds.length > 0 &&
		(assigneeIds.includes(user.id) || isAdminOrManager);

	const { data, error, isLoading, mutate } = useApiSWR<TimeLog[]>(
		["tasks", taskId, "time-logs"],
		() => listTimeLogs(projectId, taskId),
	);
	const logs = useMemo(() => data ?? [], [data]);
	const [expanded, setExpanded] = useState(false);
	const [composing, setComposing] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const totalMinutes = useMemo(
		() => logs.reduce((sum, l) => sum + (l.duration_minutes || 0), 0),
		[logs],
	);

	async function create(v: TimeLogValues) {
		const created = await createTimeLog(projectId, taskId, toPayload(v));
		await mutate((curr) => [created, ...(curr ?? [])], {
			revalidate: false,
		});
		setComposing(false);
		setExpanded(true);
		onChange?.();
	}

	async function update(logId: string, v: TimeLogValues) {
		const updated = await updateTimeLog(
			projectId,
			taskId,
			logId,
			toPayload(v),
		);
		await mutate(
			(curr) => (curr ?? []).map((l) => (l.id === logId ? updated : l)),
			{ revalidate: false },
		);
		onChange?.();
	}

	async function remove(logId: string) {
		setDeletingId(logId);
		try {
			await deleteTimeLog(projectId, taskId, logId);
			await mutate((curr) => (curr ?? []).filter((l) => l.id !== logId), {
				revalidate: false,
			});
			onChange?.();
		} catch (e) {
			reportError(e);
		} finally {
			setDeletingId(null);
		}
	}

	function canTouchLog(log: TimeLog): boolean {
		if (!user) return false;
		if (log.logged_by?.id === user.id || log.user_id === user.id)
			return true;
		return isAdminOrManager;
	}

	return (
		<div className="space-y-2">
			<div className="flex items-center justify-between gap-2">
				<button
					type="button"
					onClick={() => setExpanded((v) => !v)}
					disabled={isLoading || logs.length === 0}
					aria-expanded={expanded}
					className={cn(
						"-mx-1 flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left text-sm text-foreground transition-colors",
						logs.length > 0 && "hover:bg-muted-subtle/60",
					)}
				>
					{logs.length > 0 && (
						<ChevronRight
							className={cn(
								"h-3.5 w-3.5 shrink-0 text-muted transition-transform",
								expanded && "rotate-90",
							)}
						/>
					)}
					{isLoading ? (
						<span className="text-muted">Loading…</span>
					) : logs.length === 0 ? (
						<span className="text-muted">None logged</span>
					) : (
						<span className="truncate">
							{formatTime(totalMinutes)}
							<span className="text-xs text-muted">
								{" "}
								· {logs.length}{" "}
								{logs.length === 1 ? "entry" : "entries"}
							</span>
						</span>
					)}
				</button>
				{canLog && (
					<Popover open={composing} onOpenChange={setComposing}>
						<PopoverTrigger asChild>
							<Button size="sm" variant="outline" className="px-2">
								<Plus className="h-3.5 w-3.5" />
								Log
							</Button>
						</PopoverTrigger>
						<PopoverContent
							align="end"
							className="w-80 p-3"
							onEscapeKeyDown={(e) => e.stopPropagation()}
						>
							<p className="mb-3 text-sm font-medium text-foreground">
								Log time
							</p>
							<TimeLogForm
								submitLabel="Log time"
								onCancel={() => setComposing(false)}
								onSubmit={create}
							/>
						</PopoverContent>
					</Popover>
				)}
			</div>

			{error && (
				<p className="text-xs text-danger">
					Couldn't load time logs.{" "}
					<button
						type="button"
						className="underline"
						onClick={() => mutate()}
					>
						Retry
					</button>
				</p>
			)}

			{!canLog && !isLoading && assigneeIds.length === 0 && (
				<p className="text-[11px] text-muted">
					Assign a user to enable time logging.
				</p>
			)}

			{expanded && logs.length > 0 && (
				<ul className="divide-y divide-border rounded-md border border-border bg-surface">
					{logs.map((log) => (
						<TimeLogRow
							key={log.id}
							log={log}
							editable={canTouchLog(log)}
							deleting={deletingId === log.id}
							onUpdate={(v) => update(log.id, v)}
							onDelete={() => remove(log.id)}
						/>
					))}
				</ul>
			)}
		</div>
	);
}
